import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { getApiUrl } from "../../config/api";
import { BookOpen, Plus, Edit2, Trash2, Search, X, Check, ChevronLeft, ChevronRight, Download, Square, CheckSquare } from "lucide-react";
import "../../styles/Dashboard.css";

interface SubjectType {
  id: string;
  name: string;
  classNames?: string[];
}

interface GlobalSubjectOption {
  id: string;
  name: string;
  imported?: boolean;
}

const AllSubjects: React.FC = () => {
  const [subjects, setSubjects] = useState<SubjectType[]>([]);
  const [name, setName] = useState("");
  const [editingSubject, setEditingSubject] = useState<SubjectType | null>(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error">("success");
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [globalSearchTerm, setGlobalSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);
  const [globalSubjects, setGlobalSubjects] = useState<GlobalSubjectOption[]>([]);
  const [selectedGlobalSubjectIds, setSelectedGlobalSubjectIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const token = localStorage.getItem("token");
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const canManageImports = role === "admin";
  const canDelete = role === "superadmin" || role === "admin";
  const canEditSubject = role === "superadmin";
  const canCreateSubject = role === "superadmin";

  useEffect(() => {
    fetchSubjects();
  }, [token]);

  const fetchSubjects = async () => {
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl("academics/subjects"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const subjectsData = Array.isArray(res.data)
        ? res.data
        : res.data.subjects || [];
      setSubjects(subjectsData);
    } catch (err) {
      console.error("Error fetching subjects:", err);
      showMessage("Error fetching subjects", "error");
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg: string, type: "success" | "error" = "success") => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(""), 3000);
  };

  const handleAddSubject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreateSubject) {
      showMessage("Only superadmin can create new subjects. Please import from global templates.", "error");
      return;
    }
    if (!name.trim()) return;

    setIsSubmitting(true);
    try {
      await axios.post(
        getApiUrl("academics/subjects"),
        { name: name.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showMessage("Subject added successfully", "success");
      setName("");
      await fetchSubjects();
    } catch (err: any) {
      showMessage(err.response?.data?.message || "Error adding subject", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchGlobalSubjects = async () => {
    try {
      const res = await axios.get(
        getApiUrl("academics/subjects/global"),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const subjectsData = Array.isArray(res.data) ? res.data : res.data.subjects || [];
      setGlobalSubjects(subjectsData.map((subject: any) => ({
        id: String(subject.id),
        name: subject.name,
        imported: Boolean(subject.imported)
      })));
    } catch {
      setGlobalSubjects([]);
    }
  };

  const openImportModal = async () => {
    setSelectedGlobalSubjectIds(new Set());
    setGlobalSearchTerm("");
    await fetchGlobalSubjects();
    setShowImportModal(true);
  };

  const handleToggleGlobalSubject = (subjectId: string) => {
    setSelectedGlobalSubjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(subjectId)) {
        next.delete(subjectId);
      } else {
        next.add(subjectId);
      }
      return next;
    });
  };

  const handleImportSubjects = async () => {
    if (selectedGlobalSubjectIds.size === 0) {
      showMessage("Select at least one subject to import", "error");
      return;
    }

    setIsImporting(true);
    try {
      const selectedSubjects = globalSubjects.filter((subject) => selectedGlobalSubjectIds.has(subject.id));
      const selectedSubjectIds = Array.from(selectedGlobalSubjectIds);
      const res = await axios.post(
        getApiUrl("academics/subjects/import/bulk"),
        {
          subjectIds: selectedSubjectIds,
          subjectNames: selectedSubjects.map((subject) => subject.name)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showMessage(res.data?.message || "Subjects imported successfully", "success");
      setShowImportModal(false);
      setSelectedGlobalSubjectIds(new Set());
      await fetchSubjects();
    } catch (err: any) {
      showMessage(err.response?.data?.message || "Error importing subjects", "error");
    } finally {
      setIsImporting(false);
    }
  };

  const handleEditClick = (subject: SubjectType) => {
    setEditingSubject(subject);
    setName(subject.name);
    setShowModal(true);
  };

  const handleUpdateSubject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !editingSubject) return;

    setIsSubmitting(true);
    try {
      await axios.put(
        getApiUrl(`academics/subjects/${editingSubject.id}`),
        { name: name.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showMessage("Subject updated successfully", "success");
      setShowModal(false);
      setEditingSubject(null);
      setName("");
      await fetchSubjects();
    } catch (err: any) {
      showMessage(err.response?.data?.message || "Error updating subject", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (subject: SubjectType) => {
    if (!canDelete) return;
    setDeleteConfirm({ id: subject.id, name: subject.name });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;

    try {
      await axios.delete(
        getApiUrl(`academics/subjects/${deleteConfirm.id}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showMessage("Subject deleted successfully", "success");
      setDeleteConfirm(null);
      await fetchSubjects();
    } catch (err: any) {
      showMessage(err.response?.data?.message || "Error deleting subject", "error");
      setDeleteConfirm(null);
    }
  };

  const handleCancelEdit = () => {
    setShowModal(false);
    setEditingSubject(null);
    setName("");
  };

  const filteredSubjects = subjects.filter((s) =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredGlobalSubjects = useMemo(
    () => globalSubjects.filter((s) => s.name.toLowerCase().includes(globalSearchTerm.toLowerCase())),
    [globalSearchTerm, globalSubjects]
  );
  const selectableFilteredGlobalSubjects = useMemo(
    () => filteredGlobalSubjects.filter((subject) => !subject.imported),
    [filteredGlobalSubjects]
  );
  const allSelectableFilteredSubjectsSelected = selectableFilteredGlobalSubjects.length > 0
    && selectableFilteredGlobalSubjects.every((subject) => selectedGlobalSubjectIds.has(subject.id));

  const handleToggleAllGlobalSubjects = () => {
    if (selectableFilteredGlobalSubjects.length === 0) {
      return;
    }

    setSelectedGlobalSubjectIds((prev) => {
      const next = new Set(prev);

      if (allSelectableFilteredSubjectsSelected) {
        selectableFilteredGlobalSubjects.forEach((subject) => next.delete(subject.id));
      } else {
        selectableFilteredGlobalSubjects.forEach((subject) => next.add(subject.id));
      }

      return next;
    });
  };
  // Pagination logic
  const totalPages = Math.ceil(filteredSubjects.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentSubjects = filteredSubjects.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("...");
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };
  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  if (loading) {
    return (
      <div className="dashboard-content w-full">
        <div className="main-content px-6 py-6">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mb-4"></div>
              <p className="text-gray-600 font-medium">Loading subjects...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-content w-full">
      <div className="main-content px-6 py-6">
        {/* Header */}
        <header className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <BookOpen className="text-teal-600" size={28} />
              Subjects Management
            </h1>
            <p className="text-gray-500 mt-1">
              {canCreateSubject
                ? "Create, edit, and manage the shared global subject catalog."
                : "Import and manage the subjects this school uses from the global catalog."}
            </p>
          </div>
          {canCreateSubject ? null : canManageImports ? (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-700">
                Choose the specific global subjects this school should use.
              </div>
              <button
                type="button"
                onClick={openImportModal}
                disabled={isImporting}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download size={16} />
                {isImporting ? "Importing..." : "Import Subjects"}
              </button>
            </div>
          ) : (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-700">
              Subject imports are managed by school admins.
            </div>
          )}
        </header>

        {!canCreateSubject && (
          <div className="mb-6 rounded-lg border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-800">
            Only imported subjects appear here. After importing them, assign the needed ones to classes from the class subject pages.
          </div>
        )}

        {/* Message Alert */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center justify-between ${messageType === "success"
            ? "bg-green-50 border border-green-200 text-green-800"
            : "bg-red-50 border border-red-200 text-red-800"
            }`}>
            <span className="flex items-center gap-2">
              {messageType === "success" ? (
                <Check className="text-green-600" size={20} />
              ) : (
                <X className="text-red-600" size={20} />
              )}
              {message}
            </span>
            <button onClick={() => setMessage("")} className="text-gray-400 hover:text-gray-600">
              <X size={18} />
            </button>
          </div>
        )}

        {(canCreateSubject || editingSubject) && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Add/Edit Form Card */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Plus className="text-teal-600" size={20} />
                  {editingSubject ? "Edit Subject" : "Add New Subject"}
                </h2>
                <form onSubmit={editingSubject ? handleUpdateSubject : handleAddSubject}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject Name
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., Mathematics, English"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition"
                      required
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-teal-600 text-white font-medium py-2 px-4 rounded-lg hover:bg-teal-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Saving...</span>
                        </>
                      ) : editingSubject ? (
                        <>
                          <Check size={16} />
                          <span>Update</span>
                        </>
                      ) : (
                        <>
                          <Plus size={16} />
                          <span>Add Subject</span>
                        </>
                      )}
                    </button>
                    {editingSubject && (
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* Subjects List Card */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-800">
                    All Subjects ({filteredSubjects.length})
                  </h2>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      placeholder="Search subjects..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none w-64"
                    />
                  </div>
                </div>
                {filteredSubjects.length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            #
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Subject Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Classes
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {currentSubjects.map((subject, index) => (
                            <tr key={subject.id} className="hover:bg-gray-50 transition">
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                {indexOfFirstItem + index + 1}
                              </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <BookOpen className="text-teal-600" size={18} />
                                <span className="text-base font-bold text-gray-900">{subject.name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="flex flex-wrap gap-2">
                                {(subject.classNames && subject.classNames.length > 0) ? (
                                  subject.classNames.map((cls) => (
                                    <span key={`${subject.id}-${cls}`} className="px-2 py-1 text-xs font-semibold bg-slate-100 text-slate-700 rounded-full">
                                      {cls}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-sm text-slate-400">Not assigned</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end gap-2">
                                {canEditSubject && (
                                  <button
                                    onClick={() => handleEditClick(subject)}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                    title="Edit subject"
                                  >
                                    <Edit2 size={18} />
                                  </button>
                                )}
                                {canDelete && (
                                  <button
                                    onClick={() => handleDeleteClick(subject)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                                    title={canCreateSubject ? "Delete subject" : "Unimport subject"}
                                  >
                                    <Trash2 size={18} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {totalPages > 1 && (
                      <div className="mt-6 flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                          Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredSubjects.length)} of {filteredSubjects.length} subjects
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                          >
                            <ChevronLeft size={16} />
                            Previous
                          </button>
                          <div className="flex items-center gap-1">
                            {getPageNumbers().map((page, index) => (
                              <React.Fragment key={index}>
                                {page === "..." ? (
                                  <span className="px-2 text-gray-400">...</span>
                                ) : (
                                  <button
                                    onClick={() => paginate(page as number)}
                                    className={`px-3 py-2 rounded-lg transition ${currentPage === page
                                      ? "bg-teal-600 text-white"
                                      : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                                      }`}
                                  >
                                    {page}
                                  </button>
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                          <button
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                          >
                            Next
                            <ChevronRight size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12">
                    <BookOpen className="mx-auto text-gray-400 mb-4" size={48} />
                    <p className="text-gray-500 text-lg">
                      {searchTerm ? "No subjects found matching your search." : "No shared subjects found yet."}
                    </p>
                    {!searchTerm && canManageImports && (
                      <button
                        type="button"
                        onClick={openImportModal}
                        disabled={isImporting}
                        className="mt-4 inline-flex items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-700 hover:bg-teal-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Download size={16} />
                        {isImporting ? "Importing..." : "Import Subjects"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {!(canCreateSubject || editingSubject) && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 w-full">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-800">
                All Subjects ({filteredSubjects.length})
              </h2>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search subjects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none w-64"
                />
              </div>
            </div>

            {filteredSubjects.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          #
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Subject Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Classes
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentSubjects.map((subject, index) => (
                        <tr key={subject.id} className="hover:bg-gray-50 transition">
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            {indexOfFirstItem + index + 1}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <BookOpen className="text-teal-600" size={18} />
                              <span className="text-base font-bold text-gray-900">{subject.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex flex-wrap gap-2">
                              {(subject.classNames && subject.classNames.length > 0) ? (
                                subject.classNames.map((cls) => (
                                  <span key={`${subject.id}-${cls}`} className="px-2 py-1 text-xs font-semibold bg-slate-100 text-slate-700 rounded-full">
                                    {cls}
                                  </span>
                                ))
                              ) : (
                                <span className="text-sm text-slate-400">Not assigned</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-2">
                              {canEditSubject && (
                                <button
                                  onClick={() => handleEditClick(subject)}
                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                  title="Edit subject"
                                >
                                  <Edit2 size={18} />
                                </button>
                              )}
                              {canDelete && (
                                <button
                                  onClick={() => handleDeleteClick(subject)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                                  title={canCreateSubject ? "Delete subject" : "Unimport subject"}
                                >
                                  <Trash2 size={18} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredSubjects.length)} of {filteredSubjects.length} subjects
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        <ChevronLeft size={16} />
                        Previous
                      </button>
                      <div className="flex items-center gap-1">
                        {getPageNumbers().map((page, index) => (
                          <React.Fragment key={index}>
                            {page === "..." ? (
                              <span className="px-2 text-gray-400">...</span>
                            ) : (
                              <button
                                onClick={() => paginate(page as number)}
                                className={`px-3 py-2 rounded-lg transition ${currentPage === page
                                  ? "bg-teal-600 text-white"
                                  : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                                  }`}
                              >
                                {page}
                              </button>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                      <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        Next
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <BookOpen className="mx-auto text-gray-400 mb-4" size={48} />
                <p className="text-gray-500 text-lg">
                  {searchTerm ? "No subjects found matching your search." : "No shared subjects found yet."}
                </p>
                {!searchTerm && canManageImports && (
                  <button
                    type="button"
                    onClick={openImportModal}
                    disabled={isImporting}
                    className="mt-4 inline-flex items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-700 hover:bg-teal-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download size={16} />
                    {isImporting ? "Importing..." : "Import Subjects"}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {showImportModal && canManageImports && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Import Subjects</h3>
                  <p className="text-sm text-gray-600 mt-1">Select the global subjects this school should import.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowImportModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  value={globalSearchTerm}
                  onChange={(e) => setGlobalSearchTerm(e.target.value)}
                  placeholder="Search global subjects..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                />
              </div>

              <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <div className="text-sm text-gray-600">
                  {selectableFilteredGlobalSubjects.length} available subject{selectableFilteredGlobalSubjects.length === 1 ? "" : "s"} in this view
                </div>
                <button
                  type="button"
                  onClick={handleToggleAllGlobalSubjects}
                  disabled={selectableFilteredGlobalSubjects.length === 0}
                  className="inline-flex items-center gap-2 rounded-lg border border-teal-200 bg-white px-3 py-2 text-sm font-medium text-teal-700 hover:bg-teal-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {allSelectableFilteredSubjectsSelected ? (
                    <CheckSquare size={16} />
                  ) : (
                    <Square size={16} />
                  )}
                  {allSelectableFilteredSubjectsSelected ? "Clear selection" : "Select all"}
                </button>
              </div>

              <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
                {filteredGlobalSubjects.length > 0 ? (
                  filteredGlobalSubjects.map((subject) => {
                    const isImported = Boolean(subject.imported);
                    const isSelected = selectedGlobalSubjectIds.has(subject.id);
                    return (
                      <button
                        key={subject.id}
                        type="button"
                        onClick={() => !isImported && handleToggleGlobalSubject(subject.id)}
                        disabled={isImported}
                        className={`w-full flex items-center gap-3 px-4 py-3 border-b border-gray-100 last:border-b-0 text-left transition ${
                          isImported ? "bg-gray-50 text-gray-400 cursor-not-allowed" : "hover:bg-teal-50"
                        }`}
                      >
                        {isImported ? (
                          <CheckSquare size={18} className="text-gray-300" />
                        ) : isSelected ? (
                          <CheckSquare size={18} className="text-teal-600" />
                        ) : (
                          <Square size={18} className="text-gray-400" />
                        )}
                        <span className="font-medium">{subject.name}</span>
                        {isImported && <span className="ml-auto text-xs font-semibold uppercase tracking-wider">Imported</span>}
                      </button>
                    );
                  })
                ) : (
                  <div className="px-4 py-10 text-center text-sm text-gray-500">
                    No global subjects found.
                  </div>
                )}
              </div>

              <div className="mt-4 text-sm text-gray-600">
                {selectedGlobalSubjectIds.size} subject{selectedGlobalSubjectIds.size === 1 ? "" : "s"} selected
              </div>

              <div className="mt-6 flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleImportSubjects}
                  disabled={isImporting || selectedGlobalSubjectIds.size === 0}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isImporting ? "Importing..." : "Import Selected"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">{canCreateSubject ? "Confirm Delete" : "Confirm Unimport"}</h3>
              <p className="text-gray-600 mb-6">
                {canCreateSubject
                  ? <>Are you sure you want to delete the subject <strong>"{deleteConfirm.name}"</strong>? This action cannot be undone.</>
                  : <>Are you sure you want to remove <strong>"{deleteConfirm.name}"</strong> from this school? The global template will remain available for future import.</>}
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-2"
                >
                  <Trash2 size={16} />
                  {canCreateSubject ? "Delete" : "Unimport"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllSubjects;
